import { useEffect, useRef } from 'react';

/**
 * useModalFocus — shared focus-management + ESC-capture hook for all modal dialogs.
 *
 * Handles:
 *  1. Immediate focus on the dialog container (`tabIndex={-1}`) so screen readers
 *     announce the dialog role/label on open.
 *  2. Deferred (50 ms) focus on the first focusable descendant (input, select,
 *     textarea, button, link, or [tabindex] element) so the user can start typing
 *     immediately without an extra Tab press.
 *  3. Capture-phase `keydown` listener for `Escape` that calls `onClose` and calls
 *     `e.stopPropagation()` so nested modals handle ESC independently.
 *  4. Focus restoration to the element that was active when the modal opened,
 *     via `requestAnimationFrame` so the DOM has settled before the focus call.
 *
 * Usage:
 *   const dialogRef = useRef<HTMLDivElement>(null);
 *   useModalFocus(isOpen, onClose, dialogRef);
 *
 * @param isOpen    Whether the modal is currently mounted/visible.
 * @param onClose   Callback to close the modal (called on ESC).
 * @param dialogRef Ref attached to the element that has `role="dialog"` and `tabIndex={-1}`.
 * @param options   Configuration options for the hook.
 *
 * Deferred focus precedence (first non-null wins):
 *  1. options.focusTargetRef.current (caller-provided explicit target)
 *  2. dialogRef.current.querySelector('[data-autofocus="true"]') (DOM-marked target)
 *  3. dialogRef.current.querySelector('button, input, textarea, ...') (first focusable)
 */
export function useModalFocus(
  isOpen: boolean,
  onClose: () => void,
  dialogRef: React.RefObject<HTMLElement>,
  options?: { focusTargetRef?: React.RefObject<HTMLElement> | null }
): void {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    // Store the element that had focus before the modal opened
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Immediately focus the dialog container so AT announces dialog role/label
    dialogRef.current?.focus();

    // Deferred focus on the target or first interactive element inside the dialog
    const timer = setTimeout(() => {
      const target = options?.focusTargetRef?.current
        || dialogRef.current?.querySelector<HTMLElement>(
        '[data-autofocus="true"]'
      ) || dialogRef.current?.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      target?.focus();
    }, 50);

    // Capture-phase ESC — runs before other keydown handlers (e.g. parent modals)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown, true);
      // Restore focus to the element that was focused before the modal opened
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        requestAnimationFrame(() => previouslyFocused.focus());
      }
    };
  }, [isOpen, dialogRef, options?.focusTargetRef]);
} 
